import React, {
  useContext,
  useEffect,
  useRef,
  useState,
  useMemo,
  useCallback,
} from 'react';
import { observer } from 'mobx-react';
import { compose } from 'recompose';
import PropTypes from 'prop-types';
import format from 'string-template';
import { getFirebase } from 'firestorter';
import { CircularProgress } from '@mui/material';

import { ReactComponent as RestrictionIcon } from '../../../../../../assets/icons/v2/restriction.svg';
import {
  Card,
  HeaderContainer,
  TitleContainer,
  Title,
  Container,
} from '../../../../../../components/v2/Card';
import { DarkTag as RestrictionTag } from '../../../../../../components/Tags';
import InputLabel from '../../../../../../components/v2/InputLabel';
import { SaveButton } from '../../../../../../components/Button/ActionButtons';
import useComponentMounted from '../../../../../../hooks/useComponentMounted';
import useSessionStore from '../../../../../../hooks/useSessionStore';
import ExternalCoachContext from '../../../../../context/ExternalCoachContext';
import FirebaseContext from '../../../../../../context/FirebaseContext';
import useToast from '../../../../../hooks/useToast';
import UserNutritionProfile, {
  DietaryRestriction,
  DietaryRestrictionText,
} from '../../../../../Model/UserNutritionProfile';
import MealPlanAssignment from '../../../../../Model/MealPlanAssignment';
import User from '../../../../../../Model/User';
import { MealPlanType } from '../../../../../Model/MealPlan';
import MultiSelectFilter from '../../../../MultiSelectFilter';
import ConfirmDialog from '../../../../ConfirmDialog';
import { SecondaryText } from '../styles';

import IngredientExclusionsSection from './IngredientExclusions';
import {
  StyledLoadingPage,
  RestrictionSettingsContainer,
  RestrictionTagContainer,
  SaveRow,
  RefreshLink,
} from './styles';
import texts from './texts.json';

const dietaryRestrictions = Object.values(DietaryRestriction)
  .map((tag) => ({
    value: tag,
    label: DietaryRestrictionText[tag],
  }))
  .sort((a, b) => a.label.localeCompare(b.label));

const NutritionSettings = ({ clientId, coachId }) => {
  const isComponentMountedRef = useComponentMounted();
  const { showToast } = useToast();
  const { isCoachAssistant } = useSessionStore();
  const { coachDoc } = useContext(ExternalCoachContext);
  const { firebase: { remote } } = useContext(FirebaseContext);

  const [nutritionProfile, setNutritionProfile] = useState(null);
  const [restrictions, setRestrictions] = useState([]);
  const [excludedIngredients, setExcludedIngredients] = useState([]);
  const [currentAssignmentType, setCurrentAssignmentType] = useState(null);
  const [currentAssignmentProductId, setCurrentAssignmentProductId] = useState('');
  const [isReady, setIsReady] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [showRefreshButton, setShowRefreshButton] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const baselineRef = useRef({ restrictions: [], excludedIngredients: [] });

  useEffect(() => {
    const init = async () => {
      const [nutritionProfileDoc, user] = await Promise.all([
        UserNutritionProfile.getById(clientId),
        // Scoped to this coach, whose products decide which plans survive the filter below.
        User.getById(clientId, { viewAsCoachId: coachId }),
      ]);

      /*
        Dietary restrictions are per-client, so this refreshes the client's most recent plan — but
        the user doc is needed to keep a cancelled product's plan out of the running, since those
        stay `current: true` once demotion is per product.
      */
      const currentAssignments = await MealPlanAssignment.getUserMealPlanAssignments(clientId, user, coachId);
      const [currentAssignment] = currentAssignments;

      if (isComponentMountedRef.current) {
        const loadedRestrictions = nutritionProfileDoc?.dietaryRestrictions || [];
        const loadedExclusions = nutritionProfileDoc?.excludedIngredients || [];
        setNutritionProfile(nutritionProfileDoc);
        setRestrictions(loadedRestrictions);
        setExcludedIngredients(loadedExclusions);
        setCurrentAssignmentType(currentAssignment?.type || null);
        setCurrentAssignmentProductId(currentAssignment?.productId || '');
        baselineRef.current = { restrictions: loadedRestrictions, excludedIngredients: loadedExclusions };
        setIsReady(true);
      }
    };

    init();
  }, [isComponentMountedRef, clientId, coachId, isCoachAssistant]);

  const canShowRefreshButton = isCoachAssistant
    && !!coachDoc?.isACEnabled
    && !!coachDoc?.mealPlanConfig?.isAutoRefreshEnabled
    && currentAssignmentType === MealPlanType.LIVE;

  const tags = useMemo(() => restrictions.map((option) => (
    <RestrictionTag key={option}>
      {DietaryRestrictionText[option]}
    </RestrictionTag>
  )), [restrictions]);

  const handleSave = useCallback(async () => {
    setShowConfirmationModal(false);
    try {
      await nutritionProfile.updateFields({
        dietaryRestrictions: restrictions,
        excludedIngredients,
        lastUpdatedAt: getFirebase().default.firestore.FieldValue.serverTimestamp(),
      });
      showToast(texts.success);

      const { restrictions: baselineRestrictions, excludedIngredients: baselineExclusions } = baselineRef.current;
      const changed = JSON.stringify(restrictions) !== JSON.stringify(baselineRestrictions)
        || JSON.stringify(excludedIngredients) !== JSON.stringify(baselineExclusions);

      baselineRef.current = { restrictions, excludedIngredients };
      setShowRefreshButton(changed && canShowRefreshButton);
    } catch (error) {
      showToast(format(texts.fail, { error }), { error: true });
    }
  }, [showToast, nutritionProfile, restrictions, excludedIngredients, canShowRefreshButton]);

  const handleRefreshMealPlan = useCallback(async () => {
    setIsRefreshing(true);
    try {
      const response = await remote('refreshMealPlan', {
        userId: clientId,
        ...(currentAssignmentProductId && { productId: currentAssignmentProductId }),
      });
      if (!response.ok) {
        throw new Error(response.statusText);
      }
      showToast(texts.refreshMealPlanQueued);
      setShowRefreshButton(false);
    } catch (error) {
      showToast(format(texts.refreshMealPlanError, { error }), { error: true });
    } finally {
      setIsRefreshing(false);
    }
  }, [remote, clientId, currentAssignmentProductId, showToast]);

  if (!isReady) {
    return <StyledLoadingPage />;
  }

  return (
    <Card>
      <HeaderContainer>
        <TitleContainer>
          <Title>{texts.title}</Title>
        </TitleContainer>
      </HeaderContainer>
      {!nutritionProfile ? <SecondaryText>{texts.noProfile}</SecondaryText>
        : (
          <>
            <Container>
              <InputLabel>{texts.restrictions}</InputLabel>
              <RestrictionSettingsContainer>
                <MultiSelectFilter
                  description={texts.filterDescription}
                  initialValues={restrictions}
                  options={dietaryRestrictions}
                  onValuesSelected={setRestrictions}
                  filterIcon={<RestrictionIcon />}
                  hideAllOption
                  disableValueRender
                />
                <RestrictionTagContainer>
                  {tags.length ? tags : <SecondaryText>{texts.noTags}</SecondaryText>}
                </RestrictionTagContainer>
              </RestrictionSettingsContainer>
              <IngredientExclusionsSection
                clientId={clientId}
                value={excludedIngredients}
                onChange={setExcludedIngredients}
              />
            </Container>
            <SaveRow>
              <SaveButton
                onClick={() => setShowConfirmationModal(true)}
                $fitToContent
              >
                {texts.save}
              </SaveButton>
              {showRefreshButton && (
                <RefreshLink
                  onClick={handleRefreshMealPlan}
                  disabled={isRefreshing}
                >
                  {isRefreshing ? <CircularProgress size={20} /> : texts.refreshMealPlanLink}
                </RefreshLink>
              )}
            </SaveRow>
          </>
        )}
      <ConfirmDialog
        isOpen={showConfirmationModal}
        onConfirm={handleSave}
        onCancel={() => setShowConfirmationModal(false)}
        dialogTexts={{
          title: texts.modalTitle,
          content: texts.modalContent,
        }}
      />
    </Card>
  );
};

NutritionSettings.propTypes = {
  clientId: PropTypes.string.isRequired,
  coachId: PropTypes.string.isRequired,
};

export default compose(
  observer,
)(NutritionSettings);
